import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PriceBreakdown } from '../api/booking/pricePreviewApiTypes';
import { priceOverrideIsSaveable } from '../utils/priceAdjustmentGuard';

type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error';

type ShowSnackbar = (data: {
    open: boolean;
    message: string;
    severity: SnackbarSeverity;
}) => void;

/**
 * Blocks a save whose price override would re-bill the booking to 0,00 €, and
 * tells the operator what to change — see `priceOverrideIsSaveable` for why
 * refusing is the only honest answer and why it cannot live in the backend.
 *
 * A hook rather than a bare call at each site because both dialogs otherwise
 * repeat the same check-and-report block verbatim; the guard belongs in one
 * place along with the message it raises.
 *
 * @param showSnackbar the page's own snackbar setter — identical shape in both
 *                     edit dialogs
 * @returns a predicate to call before building the payload: `false` means the
 *          save was refused and the operator has already been told
 */
export const usePriceOverrideGuard = (showSnackbar: ShowSnackbar) => {
    const { t } = useTranslation();

    return useCallback(
        (breakdown: Pick<PriceBreakdown, 'total'> | null | undefined): boolean => {
            if (priceOverrideIsSaveable(breakdown)) return true;
            showSnackbar({
                open: true,
                message: t('edit_booking.response.negative_total'),
                severity: 'error',
            });
            return false;
        },
        [showSnackbar, t],
    );
};
